import { trimWhiteSpace } from '@perch-labs/warbler';
import { isInactive } from 'domain/plans';
import { copyFor } from 'config';

interface SelectOption {
  text: string;
  value: number;
}

const toDropdownOption = (
  utilityAccountAndPlan: UtilityAccountAndPlan
): SelectOption => ({
  text: getUtilityAccountDisplayName(utilityAccountAndPlan),
  value: utilityAccountAndPlan.utilityAccount.id,
});

const allUtilityAccountAndPlans = (
  account: AccountInfo
): UtilityAccountAndPlan[] => {
  const utilityAccountAndPlans: UtilityAccountAndPlan[] = [];
  account.utilityAccounts.forEach(utilityAccount => {
    const plan = account.customPlans.find(plan =>
      plan.utilityAccounts.some(ua => utilityAccount.id === ua.id)
    );
    utilityAccountAndPlans.push({
      plan,
      utilityAccount,
    });
  });
  return utilityAccountAndPlans;
};

export const utilityAccountSelectOptions = (
  account: AccountInfo
): SelectOption[] => {
  return allUtilityAccountAndPlans(account).map(toDropdownOption);
};

export const findUtilityAccountAndPlan = (
  account: AccountInfo,
  utilityAccountId: unknown
): UtilityAccountAndPlan | undefined => {
  return allUtilityAccountAndPlans(account).find(
    set => set.utilityAccount.id === Number(utilityAccountId)
  );
};

const getCopy = copyFor('settings.moving.selectAccount');

export const getUtilityAccountDisplayName = ({
  plan,
  utilityAccount,
}: UtilityAccountAndPlan): string => {
  const { accountNumber, utility } = utilityAccount;
  const accountName =
    utilityAccount.displayServiceAddress ||
    getCopy('accountDropdown.accountName.utilityAccountNumber', {
      accountNumber: accountNumber ? ` #${trimWhiteSpace(accountNumber)}` : '',
      utilityName: utility.displayName,
    });
  return isInactive(plan?.status || '')
    ? getCopy('accountDropdown.accountName.closed', { accountName })
    : getCopy('accountDropdown.accountName.active', { accountName });
};

export const buildUtilityAccountAndPlan = (
  context: MovingStateMachineContext
): UtilityAccountAndPlan | undefined => {
  if (context.selectedUtilityAccount) {
    return {
      plan: context.selectedPlan,
      utilityAccount: context.selectedUtilityAccount,
    };
  } else {
    return undefined;
  }
};

export const selectedUtilityAccountDisplayName = (
  context: MovingStateMachineContext
): string => {
  const utilityAccountAndPlan = buildUtilityAccountAndPlan(context);
  if (!utilityAccountAndPlan) {
    return '';
  }
  return getUtilityAccountDisplayName(utilityAccountAndPlan);
};
